import RedCactus from './RedCactus'
import useAuth from '../../../hooks/useAuth'
import { useLocation } from 'react-router-dom';
import './FloatOverlay.scss';
import { useEffect, useState } from 'react';
import UpgradeLicensePopup from './UpgradeLicensePopup';
import UpsellNotification from './UpsellNotification';
import SettingService from '../../../services/setting.service';
import useLocalStorage from '../../../hooks/useLocalStorage';
import trialService from '../../../services/trial.service';
import upsellNotificationService, { UpsellNotificationResponse } from '../../../services/upsellNotification.service';
import { CustomerTrialDetail } from '../../../types/customer/CustomerTrialDetail';
import useSessionStorage from '../../../hooks/useSessionStorage';
import useMasterData from '../../../hooks/useMasterData';
import { UserUpsellNotificationDetail } from '../../../types/upsell-notification/UserUpsellNotificationDetail';

function FloatOverlay() {
    const auth = useAuth();
    const location = useLocation();
    const masterData = useMasterData();
    const [customerTrialInfo, setCustomerTrialInfo] = useState<CustomerTrialDetail>();
    const [daysLeftForTrial, setDaysLeftForTrial] = useState<number>(0);
    const [isLicenseUpgradeButtonShow, setIsLicenseUpgradeButtonShow] = useState<boolean>(false);
    const [isLicenseUpgradeButtonSessionState, setIsLicenseUpgradeButtonSessionState] = useSessionStorage("isLicenseUpgradeButtonSessionState", true);
    const [isLicenseUpgradePopupOpen, setIsLicenseUpgradePopupOpen] = useLocalStorage("isLicenseUpgradePopupOpen", false);
    const [isClosing, setIsClosing] = useState(false); //this is for animation when we close upgrade popup
    const [isRedCactusEnabledInstanceLevel, setIsRedCactusEnabledInstanceLevel] = useState<boolean>(false);
    const [isRedCactusIconVisible, setIsRedCactusIconVisible] = useLocalStorage("isRedCactusIconVisible", false);
    const [isUpsellPopupShow, setIsUpsellPopupShow] = useState<boolean>(true);
    const [upsellNotifications, setUpsellNotifications] = useState<UserUpsellNotificationDetail[]>([]);

    useEffect(() => {
        if (location.pathname === "/upgrade-licenses") {
            setIsLicenseUpgradeButtonShow(false);
            setIsUpsellPopupShow(false);
        } else {
            const licenseUpgradeButtonCurrentState = JSON.parse(sessionStorage.getItem("isLicenseUpgradeButtonSessionState") ?? "{}");
            if (licenseUpgradeButtonCurrentState === false) setIsLicenseUpgradeButtonShow(false);
            else if (customerTrialInfo) checkVisibleStatusForLicenseUpgradeButton(customerTrialInfo);
        }
    }, [location.pathname])

    useEffect(() => {
        const getCustomerTrialDetails = async () => {
            try {
                const res = await trialService.getCustomerTrialDetails(auth?.tokenPayload?.CustomerId ?? 0);
                setCustomerTrialInfo(res);
                checkVisibleStatusForLicenseUpgradeButton(res);
            } catch (error) {
                setCustomerTrialInfo(undefined);
                setIsLicenseUpgradeButtonShow(false);
            }
        }

        const getRedCactusStatusInstanceLevel = async () => {
            const instanceEnabled = await SettingService.getRedCactusIsEnabled();

            if (instanceEnabled.toLocaleLowerCase() === "true") {
                setIsRedCactusEnabledInstanceLevel(true);
            } else {
                setIsRedCactusEnabledInstanceLevel(false);
            }
        };

        const getUpsellNotifications = async () => {
            try {
                const response: UpsellNotificationResponse = await upsellNotificationService.getNotificationsToShow(auth?.tokenPayload?.UserId ?? 0);
                if (response.status === "Success") {
                    setUpsellNotifications(response.notifications);
                    setIsUpsellPopupShow(response.notifications.length > 0);
                    return;
                }
                setUpsellNotifications([]);
                setIsUpsellPopupShow(false);
            } catch (err) {
                setUpsellNotifications([]);
                setIsUpsellPopupShow(false);
            }
        };

        auth?.accessToken && getCustomerTrialDetails();
        auth?.accessToken && getRedCactusStatusInstanceLevel();
        auth?.accessToken && getUpsellNotifications();

    }, [auth?.accessToken])

    const checkVisibleStatusForLicenseUpgradeButton = (_customerTrialInfo: CustomerTrialDetail) => {
        if (location.pathname === "/upgrade-licenses") {
            setIsLicenseUpgradeButtonShow(false);
            return;
        }
        if (!_customerTrialInfo.isAlreadyUpgraded) {
            if (_customerTrialInfo.trialDaysLeft === 0){
                //last day of trial
                setIsLicenseUpgradeButtonShow(true);
                setDaysLeftForTrial(1)
            }
            else if(_customerTrialInfo.trialDaysLeft < 0){
                //trial has ended
                if (_customerTrialInfo.postTrialNotifyDays - Math.abs(_customerTrialInfo.trialDaysLeft) >= 0) {
                    //post trail notify days are left
                    setIsLicenseUpgradeButtonShow(true);
                    setDaysLeftForTrial(-1)
                } else {
                    //post trail notify days are over
                    setIsLicenseUpgradeButtonShow(false);
                    setDaysLeftForTrial(0)
                }
            }
            else{
                //trial is active
                setIsLicenseUpgradeButtonShow(true);
                setDaysLeftForTrial(_customerTrialInfo.trialDaysLeft)
            }
        } else {
            setIsLicenseUpgradeButtonShow(false);
            setDaysLeftForTrial(0)
        }
    }

    const reopen = () => {
        setIsLicenseUpgradePopupOpen(true);
        setIsClosing(false);
        setIsRedCactusIconVisible(false);
    };

    const showIcon = () => {
        setIsRedCactusIconVisible(true);
        setIsLicenseUpgradePopupOpen(false);
    };

    return (
        <div>
            {(auth?.accessToken && auth?.userPermissions &&
                !location.pathname.includes("/login") && !location.pathname.includes("/customer-platform")) &&
                <>
                    {isUpsellPopupShow && (
                        <UpsellNotification
                            notifications={upsellNotifications}
                            onClose={() => setIsUpsellPopupShow(false)}
                            onSuppress={(historyId) => {
                                setUpsellNotifications((prev) => {
                                    const next = prev.filter(
                                        (n) => n.userUpsellNotificationHistoryId !== historyId
                                    );
                                    if (next.length === 0) {
                                        setIsUpsellPopupShow(false);
                                    }
                                    return next;
                                });
                            }}
                        />
                    )}

                    {(masterData?.isRedCactusActive && isRedCactusEnabledInstanceLevel && isRedCactusIconVisible) && (
                        <RedCactus setIsRedCactusIconVisible={setIsRedCactusIconVisible} />
                    )}
                    {isLicenseUpgradePopupOpen && isLicenseUpgradeButtonShow && isLicenseUpgradeButtonSessionState && (
                        <UpgradeLicensePopup
                            isOpen={isLicenseUpgradePopupOpen}
                            setIsOpen={setIsLicenseUpgradePopupOpen}
                            isClosing={isClosing}
                            setIsClosing={setIsClosing}
                            daysLeftForTrial={daysLeftForTrial}
                            setIsLicenseUpgradeButtonSessionState={setIsLicenseUpgradeButtonSessionState}
                        />
                    )}
                    <div className='floting-restore'>
                        {!isLicenseUpgradePopupOpen && isLicenseUpgradeButtonShow && isLicenseUpgradeButtonSessionState && (
                            <button
                                className="upgrade-restore"
                                aria-label="Show CIQpilot trial popover"
                                onClick={reopen}
                            >
                                <i className="bi bi-person-fill-up" aria-hidden="true" />
                            </button>
                        )}
                        {(masterData?.isRedCactusActive && isRedCactusEnabledInstanceLevel && !isRedCactusIconVisible) && (
                            <button className="restore-button" onClick={showIcon} title="RedCactus">
                                <img
                                    src={require("../../../assets/images/icon.png")}
                                    alt="Show RedCactus"
                                    className="restore-icon"
                                />
                            </button>
                        )}
                    </div>
                </>
            }
        </div>
    )
}

export default FloatOverlay;